// Client fuer die Transkriptions-API (FastAPI, Version 2.1.0).
//
// Endpunkte laut /openapi.json:
//   GET  /health                    Lebenszeichen
//   GET  /status                    Warteschlange, GPU, aktives Modell
//   GET  /stats                     Nutzungsstatistik (anonymisiert)
//   GET  /options                   Formate, Modelle, Sprachen, Limits, Features
//   GET  /models  GET /formats      Teilmengen von /options
//   GET  /jobs    GET /jobs/{id}    Auftragsliste bzw. einzelner Auftrag
//   POST /upload/                   Datei einreichen
//   POST /batches/ ...              Sammelauftraege
//
// /options ist selbstbeschreibend - die Oberflaeche baut ihre Auswahlfelder
// daraus auf, statt Formate und Modelle fest zu verdrahten.

import { TRANSCRIPTION_BASE_URL } from '../config';

const url = (path) => `${TRANSCRIPTION_BASE_URL}${path}`;

async function getJson(path, signal) {
    const response = await fetch(url(path), { signal, headers: { Accept: 'application/json' } });
    if (!response.ok) {
        const error = new Error(`${path} -> HTTP ${response.status}`);
        error.status = response.status;
        throw error;
    }
    return response.json();
}

/** Erster vorhandener Key (die API hiess frueher anders). */
function pick(source, keys, fallback) {
    for (const key of keys) {
        if (source && source[key] !== undefined && source[key] !== null) return source[key];
    }
    return fallback;
}

/** Warteschlange, laufender Auftrag, GPU-Zustand. */
export async function fetchStatus(signal) {
    const data = await getJson('/status', signal);
    const gpu = data.gpu || {};

    return {
        queueSize: pick(data, ['queue_size', 'Queue Size', 'queueSize'], 0),
        processing: Boolean(pick(data, ['processing_job', 'Processing Job', 'processing'], false)),
        active: Array.isArray(data.active) ? data.active : [],
        // jobs ist eine Zaehlung je Zustand und variiert - Keys nicht fest annehmen.
        jobs: data.jobs && typeof data.jobs === 'object' ? data.jobs : {},
        message: data.status || null,
        model: data.model || null,
        gpu: {
            visible: Boolean(gpu.visible),
            usable: Boolean(gpu.usable),
            forcedCpu: Boolean(gpu.forced_cpu),
            freeMib: gpu.free_mib ?? null,
        },
        raw: data,
    };
}

/**
 * Anonymisierte Nutzungsstatistik.
 * Liefert null, falls der Endpunkt (noch) nicht antwortet - die Oberflaeche
 * zeigt dann einen Hinweis statt eines Fehlers.
 */
export async function fetchStats(signal) {
    let data;
    try {
        data = await getJson('/stats', signal);
    } catch (error) {
        if (error.name === 'AbortError') throw error;
        return null;
    }

    return {
        jobsTotal: data.jobs_total ?? null,
        jobsFailed: data.jobs_failed ?? null,
        successRate: data.success_rate ?? null,
        audioHours: data.audio_hours ?? null,
        processingHours: data.processing_hours ?? null,
        realtimeFactor: data.realtime_factor ?? null,
        diarizedShare: data.diarized_share ?? null,
        byModel: data.by_model || {},
        byLanguage: data.by_language || {},
        byOutputType: data.by_output_type || {},
        privacy: data.privacy || null,
        raw: data,
    };
}

const mapFormat = (entry) => ({
    key: entry.key,
    label: entry.label || entry.key,
    description: entry.description || '',
    extension: entry.extension || '',
    maxqda: Boolean(entry.maxqda),
    needsTimestamps: Boolean(entry.needs_timestamps),
});

const mapModel = (entry) => ({
    key: entry.key,
    label: entry.label || entry.key,
    description: entry.description || '',
    vramMib: entry.approx_vram_mib ?? null,
    languages: entry.languages || '',
    community: Boolean(entry.community_conversion),
});

/** Formate, Modelle, Sprachen, Limits und Feature-Schalter der API. */
export async function fetchOptions(signal) {
    const data = await getJson('/options', signal);
    const limits = data.limits || {};
    const features = data.features || {};

    return {
        formats: (data.output_formats || []).map(mapFormat),
        defaultFormat: data.default_output_format || 'pdf',
        models: (data.models || []).map(mapModel),
        defaultModel: data.default_model || '',
        languages: (data.languages || []).map((l) => ({ code: l.code, label: l.label || l.code })),
        defaultLanguage: data.default_language || 'de',
        limits: {
            maxUploadMb: limits.max_upload_mb ?? null,
            maxDurationMinutes: limits.max_duration_minutes ?? null,
            maxBatchFiles: limits.max_batch_files ?? null,
            maxMailMb: limits.max_mail_mb ?? null,
            maxSpeakers: limits.max_speakers ?? null,
        },
        features: {
            diarization: features.diarization !== false,
            timestamps: features.timestamps !== false,
            batches: Boolean(features.batches),
            speakerNames: Boolean(features.speaker_names),
            initialPrompt: Boolean(features.initial_prompt),
            apiKeyRequired: Boolean(features.api_key_required),
            allowedMailDomains: features.allowed_mail_domains || null,
        },
    };
}

// --- Sammelauftraege -------------------------------------------------------
//
// Ablauf: POST /batches/ liefert eine batch_id, die bei jedem Upload mitgeht.
// Danach POST /batches/{id}/close. `expected_files` wird bewusst nicht gesetzt:
// damit wuerde sich der Auftrag selbst schliessen, sobald so viele Dateien
// angekommen sind - schlaegt ein Upload fehl, bliebe er dagegen offen haengen.
// Ein explizites close() am Ende ist in beiden Faellen eindeutig.

async function postForm(path, fields) {
    const body = new URLSearchParams();
    Object.entries(fields).forEach(([key, value]) => body.append(key, value ?? ''));

    const response = await fetch(url(path), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
    });
    if (!response.ok) {
        const detail = await response
            .json()
            .then((data) => (typeof data.detail === 'string' ? data.detail : null))
            .catch(() => null);
        throw new Error(detail || `${path} -> HTTP ${response.status}`);
    }
    return response.json().catch(() => ({}));
}

/** Legt einen Sammelauftrag an und liefert dessen ID. */
export async function createBatch({ email, label }) {
    const data = await postForm('/batches/', {
        email,
        label: label || 'Transkriptionen',
    });
    const batchId = data.batch_id || data.batchId || data.id;
    if (!batchId) {
        throw new Error('Die API hat keine batch_id zurückgegeben.');
    }
    return { batchId, raw: data };
}

/** Meldet, dass keine weiteren Dateien mehr folgen. */
export async function closeBatch(batchId) {
    return postForm(`/batches/${encodeURIComponent(batchId)}/close`, {});
}

/** Zustand eines Sammelauftrags. */
export async function fetchBatch(batchId, signal) {
    return getJson(`/batches/${encodeURIComponent(batchId)}`, signal);
}

/**
 * Datei zur Transkription einreichen.
 * Feldnamen entsprechen dem multipart-Body von POST /upload/.
 */
export async function uploadFile({
    file,
    attachmentName,
    email,
    outputType,
    diarization,
    timestamp,
    language,
    model,
    initialPrompt,
    minSpeakers,
    maxSpeakers,
    speakerNames,
    maxqda,
    batchId,
}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('attachment_name', attachmentName);
    formData.append('email', email);
    formData.append('output_type', outputType);
    formData.append('diarization', String(Boolean(diarization)));
    formData.append('timestamp', String(Boolean(timestamp)));
    formData.append('language', language || 'de');
    formData.append('model', model || '');
    formData.append('initial_prompt', initialPrompt || '');
    formData.append('min_speakers', minSpeakers || '');
    formData.append('max_speakers', maxSpeakers || '');
    formData.append('speaker_names', speakerNames || '');
    formData.append('maxqda', String(Boolean(maxqda)));
    formData.append('batch_id', batchId || '');

    const response = await fetch(url('/upload/'), { method: 'POST', body: formData });
    if (!response.ok) {
        // FastAPI liefert Fehler als {"detail": ...}
        const detail = await response
            .json()
            .then((body) => (typeof body.detail === 'string' ? body.detail : null))
            .catch(() => null);
        throw new Error(detail || `Upload fehlgeschlagen (HTTP ${response.status})`);
    }
    return response.json().catch(() => ({}));
}
